import pick from 'lodash/pick';
import _get from 'lodash/get';
import _isString from 'lodash/isString';
import {
  select,
  takeEvery,
  put,
  takeLatest,
  call
} from 'redux-saga/effects';

import axios from '../../axios-api';
import { getErrorMessage } from '../../common/utils';
import formEditorActions from './actions';
import formEditorConstants from './constants';
import formListActions from '../FormList/actions';
import formEditorSelectors from './selectors';
import notificatorActions from '../../common/notificator/actions';
import { FORM_STATUS } from '../../common/globals';

export function* formEditorInit(action) {
  try {
    const {
      formId,
      token
    } = action.payload;

    const apiClient = axios(token);

    const formUrl = `/form/${formId}`;
    const response = yield call(apiClient.get, formUrl);

    const { data: form } = response;

    const {
      __INITIAL_DATA__
    } = window;
    
    const teamName = _get(__INITIAL_DATA__, 'user.team.name', '');

    yield put(formEditorActions.formEditorInitSuccess(form, teamName));
    yield put(formListActions.clearNewFormId());
  }
  catch (error) {
    yield put(formEditorActions.formEditorInitError());
  }
}

export function* formSave(action) {
  try {
    const {
      republish,
      token
    } = action.payload;
    const apiClient = axios(token);

    const id = yield select(formEditorSelectors.getFormId);
    const form = yield select(formEditorSelectors.getForm);

    const payload = pick(
      form,
      [
        'assignTasksTo',
        'JSONSchema',
        'privacy',
        'UISchema',
        'taskSummary',
        'headerImage',
        'confirmationText',
        'buttonLabel',
        'buttonColor'
      ]
    );

    if (form.tasksDeadline !== -1) {
      payload.tasksDeadline = form.tasksDeadline;
    }
    
    payload.assignTasksTo = payload.assignTasksTo.map(user => (_isString(user) ? user : user.id));

    yield call(apiClient.patch, `/form/${id}`, payload);

    yield put(formEditorActions.formSaveSuccess());

    if (republish) {
      yield put(formEditorActions.formPublish(token));
    }

    yield put(notificatorActions.showSuccessNotification('Form updated successfully!'));
  }
  catch (error) {
    yield put(formEditorActions.formSaveError());
    const errorMessage = getErrorMessage(error, 'Failed to update form');
    yield put(notificatorActions.showErrorNotification(errorMessage));
  }
}

export function* formDelete(action) {
  try {
    const {
      token
    } = action.payload;
    const apiClient = axios(token);

    const formId = yield select(formEditorSelectors.getFormId);

    yield call(apiClient.delete, `/form/${formId}`);

    yield put(formEditorActions.formDeleteSuccess());
    yield put(notificatorActions.showSuccessNotification('Form archived successfully!'));
  }
  catch (error) {
    yield put(formEditorActions.formDeleteError());
    const errorMessage = getErrorMessage(error, 'Failed to archive the form');
    yield put(notificatorActions.showErrorNotification(errorMessage));
  }
}

export function* formPublish(action) {
  try {
    const {
      token
    } = action.payload;
    const apiClient = axios(token);

    const formId = yield select(formEditorSelectors.getFormId);
    const isSaving = yield select(formEditorSelectors.getIsSaving);
    if (!isSaving) {
      const form = yield select(formEditorSelectors.getForm);

      const payload = pick(
        form,
        [
          'assignTasksTo',
          'JSONSchema',
          'privacy',
          'UISchema',
          'taskSummary',
          'headerImage',
          'confirmationText',
          'buttonLabel',
          'buttonColor'
        ]
      );
  
      if (form.tasksDeadline !== -1) {
        payload.tasksDeadline = form.tasksDeadline;
      }
      
      payload.assignTasksTo = payload.assignTasksTo.map(user => (_isString(user) ? user : user.id));
  
      yield call(apiClient.patch, `/form/${formId}`, payload);
  
      yield put(formEditorActions.formSaveSuccess());
    }
    
    yield call(apiClient.post, `/form/${formId}/publish`);

    yield put(formEditorActions.formPublishSuccess());
    yield put(notificatorActions.showSuccessNotification('Form published successfully!'));
  }
  catch (error) {
    yield put(formEditorActions.formPublishError());
    const errorMessage = getErrorMessage(error);
    yield put(notificatorActions.showErrorNotification(errorMessage));
  }
}

export function* formPrivacyChange(action) {
  try {
    const {
      privacy
    } = action.payload;

    const token = _get(window, '__INITIAL_DATA__.token');

    const apiClient = axios(token);

    const formId = yield select(formEditorSelectors.getFormId);
    const form = yield select(formEditorSelectors.getForm);

    const payload = pick(form, ['JSONSchema', 'UISchema']);
    payload.privacy = privacy;

    yield call(apiClient.patch, `/form/${formId}`, payload);

    yield put(formEditorActions.formPrivacyChangeSuccess(privacy));

    const republish = form.status === FORM_STATUS.LIVE;
    yield put(formEditorActions.formSave(republish, token));
  }
  catch (error) {
    yield put(formEditorActions.formPrivacyChangeError());
    const errorMessage = getErrorMessage(error);
    yield put(notificatorActions.showErrorNotification(errorMessage));
  }
}

export function* formGetAssigneesSuggestions(action) {
  const {
    payload: {
      searchText,
      callback
    }
  } = action;
  
  try {
    const listId = _get(window, '__INITIAL_DATA__.listId');
    const participantsUrl = `/api/list/${listId}/participant`;
    const token = _get(window, '__INITIAL_DATA__.token');

    const apiClient = axios(token);

    const response = yield call(apiClient.get, participantsUrl);

    const {
      data: users
    } = response;

    const mappedUsers = users
      .filter(user => user.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1)
      .map(user => ({
        id: user.id,
        name: user.name
      }));

    callback(mappedUsers);
  }
  catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put(notificatorActions.showErrorNotification(errorMessage));
    callback([]);
  }
}

export default function* FormEditorSage() {
  yield takeEvery(formEditorConstants.INITIALIZE_FORM_EDITOR, formEditorInit);
  yield takeLatest(formEditorConstants.FORM_SAVE, formSave);
  yield takeLatest(formEditorConstants.FORM_DELETE, formDelete);
  yield takeLatest(formEditorConstants.FORM_PUBLISH, formPublish);
  yield takeLatest(formEditorConstants.FORM_PRIVACY_CHANGE, formPrivacyChange);
  yield takeLatest(formEditorConstants.SEARCH_FORM_TASK_ASSIGNEES, formGetAssigneesSuggestions);
}
